import React, { useEffect, useRef, useState } from "react";

import MosaicPage from "../interaction_pages/MosaicPage";
import OneChoicePage from "../interaction_pages/OneChoicePage";
import ContextPage from "../explanation_pages/ContextPage";
import ScoreBoard from "../graph_pages/ScoreBoard";
import CommandControlPage from "../interaction_pages/CommandControlPage";
import TwoDoughnutsGraphs from "../graph_pages/TwoDoughnutsGraphs";
import CustomPage from "../graph_pages/CustomPage";
import ErrorBoundary from "../other_components/ErrorBoundary";

/**
 * LE CANEVAS DU COMPOSEUR — la page telle qu'elle sera jouée.
 *
 * Ce sont les VRAIS composants de jeu, pas une imitation : la seule façon de
 * répondre à « est-ce que ça tient sur l'écran » est de le montrer. Ils sont
 * montés en `editMode`, qui pose les affordances d'édition (le crayon sur une
 * carte, la case « + ajouter ») sans permettre de jouer.
 *
 * CE QUE FONT CES AFFORDANCES A CHANGÉ. Elles ouvraient chacune une petite
 * modale par-dessus le rendu — donc par-dessus ce qu'elle modifiait. Elles
 * amènent maintenant à l'ONGLET correspondant de l'inspecteur, qui est à côté
 * et ne cache rien. Le canevas devient une surface de navigation dans les
 * réglages : on clique sur ce qu'on veut changer, là où on le voit.
 *
 * POURQUOI IL REMPLIT LA BOÎTE PLUTÔT QUE D'ÊTRE UN 16:9 À L'ÉCHELLE, contrairement
 * au canevas d'étape. Les pages de jeu sont dessinées en unités de fenêtre
 * (`100vh`, `100vw`) : dans un plateau réduit par un `transform: scale()`, ces
 * unités continueraient de mesurer la fenêtre de l'auteur et la mise en page
 * exploserait. Le cadre projeté ne peut donc pas être simulé ici — il l'est
 * pour les étapes parce que leur panneau est exprimé dans le repère de l'écran.
 */
// L'écran projeté. Les mêmes dimensions que le canevas d'étape : c'est le même
// vidéoprojecteur, il n'y a pas deux écrans de référence dans une séance.
const REFERENCE_WIDTH = 1920;
const REFERENCE_HEIGHT = 1080;
const REFERENCE_RATIO = REFERENCE_WIDTH / REFERENCE_HEIGHT;

export default function PageCanvas({ pageData, gameStatus, editMode, on = {} }) {
  const frameRef = useRef(null);
  const [frameWidth, setFrameWidth] = useState(0);

  // La largeur réelle du cadre pilote la mise à l'échelle : elle est mesurée
  // plutôt que devinée, parce qu'elle dépend de la fenêtre de l'auteur.
  useEffect(() => {
    const element = frameRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setFrameWidth(element.clientWidth));
    observer.observe(element);
    setFrameWidth(element.clientWidth);
    return () => observer.disconnect();
  }, [pageData?.type]);

  if (!pageData) return null;

  // LE MODULE TERRITOIRE NE PASSE PLUS PAR ICI. Il s'édite sur sa propre carte,
  // montée une fois pour toute la session par le composeur (cf. StepCanvas) :
  // reconstruire une scène coûte le rechargement de tout son datapack, et un
  // aperçu de plus l'aurait fait une fois de trop.
  const scale = frameWidth ? frameWidth / REFERENCE_WIDTH : 0;

  return (
    // UN BROUILLON PEUT ÊTRE INCOHÉRENT EN COURS DE ROUTE — un score référencé
    // qui n'existe pas encore dans l'atelier, une page ouverte hors de tout
    // atelier. Le composant de jeu a le droit d'y renoncer ; l'inspecteur, lui,
    // doit rester utilisable pour réparer. D'où cette barrière : le canevas
    // tombe seul, pas l'écran.
    <div className="page-canvas">
      <div className="page-canvas-frame" ref={frameRef} style={{ aspectRatio: REFERENCE_RATIO }}>
        <ErrorBoundary
          fallback={
            <div className="builder-empty" style={{ padding: 40 }}>
              Cette page ne peut pas être affichée en l'état — il manque probablement
              un score ou une décision référencée. Corrigez-la dans l'inspecteur ; le
              rendu reviendra à la prochaine modification.
            </div>
          }
        >
          {/* LE PLATEAU : l'écran projeté à sa taille réelle, réduit d'un seul
              `scale`. La page s'y monte avec sa propre feuille de style et s'y
              dimensionne toute seule, parce qu'elle s'exprime désormais par
              rapport à son écran (`--game-screen-h`) et non à la fenêtre. */}
          {scale > 0 && (
            <div className="page-canvas-stage" style={{ transform: `scale(${scale})` }}>
              <PageBody pageData={pageData} gameStatus={gameStatus} editMode={editMode} on={on} />
            </div>
          )}
        </ErrorBoundary>
      </div>
      <p className="page-canvas-caption">
        Écran projeté — {REFERENCE_WIDTH}×{REFERENCE_HEIGHT}
      </p>
    </div>
  );
}

function PageBody({ pageData, gameStatus, editMode, on }) {
  const noop = () => {};
  const common = { pageData, gameStatus, onDecisionSelect: noop };

  switch (pageData.type) {
    case "mosaic":
      return (
        <MosaicPage
          {...common}
          editMode={editMode}
          onEditDecision={on.editDecision}
          onAddDecision={on.addDecision}
          onEditScoreConfig={on.editScores}
        />
      );
    case "one-choice":
      return (
        <OneChoicePage
          {...common}
          editMode={editMode}
          onEditDecision={on.editDecision}
          onAddDecision={on.addDecision}
        />
      );
    case "command_control":
      return (
        <CommandControlPage
          {...common}
          editMode={editMode}
          onEditDecision={on.editDecision}
          onAddDecision={on.addDecision}
          onEditScoreConfig={on.editScores}
        />
      );
    case "score":
      return (
        <ScoreBoard
          {...common}
          displayButton={false}
          editMode={editMode}
          onEditGraphItem={on.editGraphs}
          onAddGraphItem={on.editGraphs}
        />
      );
    case "two_doughnuts_graphs":
      return (
        <TwoDoughnutsGraphs
          {...common}
          editMode={editMode}
          onEditScoreItem={on.editGraphs}
          onAddScoreItem={on.editGraphs}
        />
      );
    case "context":
      return <ContextPage {...common} editMode={editMode} onEditContent={on.editContent} />;
    // Un écran custom n'a pas d'affordance d'édition : il n'y a rien à
    // retoucher dessus, seulement un composant à choisir dans l'inspecteur.
    // Le montrer reste utile — c'est le seul moyen de voir un bilan de fin
    // d'atelier avant la séance, ce qui n'a jamais été possible.
    case "custom":
      // `displayButton={false}` comme pour ScoreBoard : l'aperçu montre l'écran,
      // pas le bouton qui en sort — il n'y a nulle part où aller ici.
      return <CustomPage {...common} displayButton={false} />;
    default:
      return <div className="builder-empty">Type de page inconnu : {pageData.type}</div>;
  }
}
